import { useEffect, useMemo, useRef, useState, type FocusEvent, type PointerEvent, type ReactElement, type RefObject, type SyntheticEvent } from "react";
import { Tooltip } from "../../ui/kit/index.ts";
import { SCOPE_TIP_ID, scopeIconOf, scopeTipPlace } from "./scope-icon.tsx";
import { say } from "../../words.ts";
import { cardLines, cardPlace, type CardFacts } from "./hover-card.ts";

/**
 * The chat panel's one tooltip (ADR-068's amendment of 2026-09-27, the author's ruling): hovering a scope icon
 * shows its scope's name, and a click, a tap or keyboard focus shows it too and keeps it until the icon loses focus
 * or a press lands anywhere else on the page, so nothing is shown only on hover (ADR-061). A player's name in a line
 * is named the same way, by its full name (its amendment "chat lines show the first name"), over the name. The panel
 * listens once on its body, the icons and the names carrying no handlers for it, and the handlers are made once per
 * panel, so rendering the log makes no functions; a tooltip's place is measured only when its target is hovered,
 * clicked or focused.
 */

/**
 * The tooltip showing: its words, its place in logical px of the panel's body, the element it names, and whether a
 * click or focus holds it past the pointer's leaving.
 */
interface ScopeTip {
  readonly text: string;
  readonly lines: readonly string[] | undefined;
  readonly left: number;
  readonly bottom?: number;
  readonly top?: number;
  readonly element: HTMLElement;
  readonly held: boolean;
}

/**
 * The handlers the panel's body takes, each finding the icon or the name an event came from.
 */
export interface ScopeTipHandlers {
  readonly onPointerOver: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerOut: (event: PointerEvent<HTMLElement>) => void;
  readonly onClick: (event: SyntheticEvent<HTMLElement>) => void;
  readonly onFocus: (event: FocusEvent<HTMLElement>) => void;
  readonly onBlur: (event: FocusEvent<HTMLElement>) => void;
}

/**
 * What the tooltip names when an event's target is or lies inside it: a scope icon by its scope's name, or a
 * player's name in a line by the full name it carries in `data-chat-full`.
 *
 * @param target The event's target.
 * @returns The element and the tooltip's words, `card` set when it is a name that opens the hover card, or null when
 *   the target is neither.
 * @example
 * tipTargetOf(event.target);
 * // { element: <button data-chat="name" data-chat-full="Mira Thornwood">, text: "Mira Thornwood" }
 */
export function tipTargetOf(target: EventTarget | null): { readonly element: HTMLElement; readonly text: string; readonly card?: true } | null {
  const icon = scopeIconOf(target);
  if (icon !== null) {
    return { element: icon.element, text: say(`chat.scopes.${icon.scope}`) };
  }
  const name = target instanceof Element ? target.closest<HTMLElement>("[data-chat-full]") : null;
  const full = name?.dataset["chatFull"];
  if (name === null || name === undefined || full === undefined || full === "") {
    return null;
  }
  return name.dataset["chatCard"] === undefined ? { element: name, text: full } : { element: name, text: full, card: true };
}

/**
 * The panel's tooltip: the ref its body takes, the handlers its body takes, and the tooltip to draw in the body. A
 * name in a line shows the hover card (ADR-068's amendment of 2026-10-27): its full name, and the level and status of
 * a character the page draws, which `cardOf` reads from the view when the card opens.
 *
 * @param cardOf Reads the facts of the character a full name names, or null; absent, a name shows its full name alone.
 *   It is read once, when a card opens, so a card held by focus or a tap shows the facts of its opening. It must keep
 *   its identity between renders (the store's is a property), since the handlers are made again when it changes.
 * @returns The body's ref, its handlers, and the tooltip or null.
 */
export function useScopeTip(cardOf?: (full: string) => CardFacts | null): { readonly body: RefObject<HTMLDivElement | null>; readonly handlers: ScopeTipHandlers; readonly tooltip: ReactElement | null } {
  const body = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<ScopeTip | null>(null);
  const handlers = useMemo((): ScopeTipHandlers => {
    /**
     * Measures where an element's tooltip stands in the body.
     *
     * @param element The icon or the name.
     * @param text The tooltip's words.
     * @param held Whether a click or focus holds it.
     * @param card Whether it is the hover card of a name.
     * @returns The tooltip, or null before the body is mounted.
     */
    const place = (element: HTMLElement, text: string, held: boolean, card: boolean): ScopeTip | null => {
      const box = body.current;
      if (box === null) {
        return null;
      }
      const px = Number.parseFloat(getComputedStyle(box).getPropertyValue("--px")) || 1;
      const lines = card ? cardLines(text, cardOf?.(text) ?? null) : undefined;
      const log = box.querySelector("[data-chat='log']")?.getBoundingClientRect();
      const at = lines !== undefined && lines.length > 1 && log !== undefined ? cardPlace(element.getBoundingClientRect(), log, box.getBoundingClientRect(), px, lines.length) : scopeTipPlace(element.getBoundingClientRect(), box.getBoundingClientRect(), px);
      return { text, lines, element, held, ...at };
    };
    /**
     * Shows what an icon or a name is called, held when a click or focus asks.
     *
     * @param target The event's target.
     * @param held Whether to hold it.
     */
    const show = (target: EventTarget | null, held: boolean): void => {
      const found = tipTargetOf(target);
      if (found !== null) {
        setTip((shown) => (shown !== null && shown.element === found.element ? (held && !shown.held ? { ...shown, held } : shown) : place(found.element, found.text, held, found.card === true)));
      }
    };
    return {
      /**
       * Shows the name of the icon or the full name of the name the pointer enters.
       *
       * @param event The pointer's move.
       */
      onPointerOver: (event) => show(event.target, false),
      /**
       * Hides a tooltip only hovered when the pointer leaves its element.
       *
       * @param event The pointer's move.
       */
      onPointerOut: (event) => {
        const found = tipTargetOf(event.target);
        if (found !== null && !(event.relatedTarget instanceof Node && found.element.contains(event.relatedTarget))) {
          setTip((shown) => (shown !== null && shown.element === found.element && !shown.held ? null : shown));
        }
      },
      /**
       * Shows and holds the name of the icon, or the full name of the player's own name, clicked or tapped. A scope
       * tab's click chooses the tab and another player's name starts a whisper, which names them in full in the
       * input, so neither holds anything over the log; hover and focus still name them.
       *
       * @param event The click.
       */
      onClick: (event) => {
        if (!(event.target instanceof Element && event.target.closest("[role='tab'], [data-chat='name']") !== null)) {
          show(event.target, true);
        }
      },
      /**
       * Shows and holds the name of the icon, or the full name of the name, that takes focus.
       *
       * @param event The focus.
       */
      onFocus: (event) => show(event.target, true),
      /**
       * Hides the tooltip of the element that loses focus.
       *
       * @param event The focus's leaving.
       */
      onBlur: (event) => {
        const found = tipTargetOf(event.target);
        if (found !== null) {
          setTip((shown) => (shown !== null && shown.element === found.element ? null : shown));
        }
      },
    };
  }, [cardOf]);
  const heldIcon = tip !== null && tip.held ? tip.element : null;
  useEffect(() => {
    if (heldIcon === null) {
      return undefined;
    }
    /**
     * Hides a held tooltip when a press lands anywhere but its element, which a page that keeps the focus where it
     * was would otherwise never tell the element.
     *
     * @param event The press.
     */
    const away = (event: globalThis.PointerEvent): void => {
      if (!(event.target instanceof Node && heldIcon.contains(event.target))) {
        setTip((shown) => (shown !== null && shown.element === heldIcon ? null : shown));
      }
    };
    document.addEventListener("pointerdown", away, true);
    return () => document.removeEventListener("pointerdown", away, true);
  }, [heldIcon]);
  const tooltip = tip === null ? null : <Tooltip id={SCOPE_TIP_ID} text={tip.text} lines={tip.lines !== undefined && tip.lines.length > 1 ? tip.lines : undefined} left={tip.left} bottom={tip.bottom} top={tip.top} />;
  return { body, handlers, tooltip };
}
